/** Get all sibling elements of the provided element. */
export const getSiblings = (el: Element): Element[] => {
  const { parentNode } = el;
  if (!parentNode) {
    return [];
  }

  return [...(parentNode as Element).children].filter(child => child !== el);
};

/** Create a DOM element from an HTML string. */
export const createElementFromString = (elementAsStr: string): Element => {
  const div = document.createElement('div');
  div.innerHTML = elementAsStr.trim();
  return div.firstElementChild as Element;
};

/**
 * Transition end event name. v1 sniffed vendor prefixes; every browser on the
 * support list fires the unprefixed event.
 */
export const transitionEnd = 'transitionend';

/** Trigger a window resize event. */
export const triggerResize = (): void => {
  window.dispatchEvent(new Event('resize'));
};

/** Check if a value is a plain object (not an array). */
export const isObject = (val: unknown): val is Record<string, unknown> =>
  val !== null && typeof val === 'object' && !Array.isArray(val);

export type ResponsiveValue<T> = T | Record<number | string, T>;

/**
 * Get a value from a responsive breakpoint map based on screen width, or the
 * value itself when it is not a map.
 */
export const getValueFromResponsiveMap = <T>(
  value: ResponsiveValue<T>,
  screenWidth: number,
  direction: 'up' | 'down' | 'only' = 'up',
): T | 0 => {
  if (!isObject(value)) {
    return value as T;
  }

  const map = value as Record<string, T>;
  const breakpoints = Object.keys(map)
    .map(key => parseInt(key, 10))
    .sort((a, b) => a - b);

  return breakpoints.reduce<T | 0>((currentValue, breakpoint) => {
    const isActive =
      direction === 'up' ? breakpoint <= screenWidth
      : direction === 'down' ? breakpoint > screenWidth
      : breakpoint === screenWidth;

    return isActive ? (map[breakpoint] as T) : currentValue;
  }, 0);
};

/**
 * Scroll offset shared by ScrollSpy, SmoothScroll and AnchorScroll: a number,
 * a responsive breakpoint map, or a function receiving the trigger/target.
 * Successor of v1's global `window.myApp.scrollOffset` - in v2 the fallback
 * is passed per module via its config `offset`.
 */
export type ScrollOffset =
  | ResponsiveValue<number>
  | ((trigger: Element | null, target?: Element | null) => number);

/**
 * Resolve the scroll offset for an element: `data-scroll-offset` on the
 * element wins (JSON - a number or a responsive map keyed by min-width),
 * otherwise the given fallback. Responsive maps are resolved against the
 * current window width.
 */
export const resolveScrollOffset = (
  element: Element | null,
  fallback: ScrollOffset = 0,
  target?: Element | null,
): number => {
  if (element?.hasAttribute('data-scroll-offset')) {
    try {
      const parsed = JSON.parse(element.getAttribute('data-scroll-offset') as string);
      return getValueFromResponsiveMap(parsed, window.innerWidth) || 0;
    } catch {
      return 0;
    }
  }

  if (typeof fallback === 'function') {
    return fallback(element, target) || 0;
  }

  return getValueFromResponsiveMap(fallback, window.innerWidth) || 0;
};

/** Escape special characters in a selector string. */
export const escapeSelectorName = (selector: string): string =>
  selector.replace(/(:|\.|\[|\])/g, '\\$1');
